import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ContactImportResultDto } from 'src/core/models/contact/contact-import-result.dto';
import { ContactImportStartedDto } from 'src/core/models/contact/contact-import-started.dto';

/**
 * CSV contact import endpoints. Lives in `core` rather than in the contact feature because both sides of
 * the flow need it: the contacts page starts an import, and the notification row (rendered by the
 * app-level notification list) loads the result of a finished one long after the page was left.
 */
const IMPORT_API_BASE = 'api/agentic-cx/crm-contact';

@Injectable({
  providedIn: 'root',
})
export class ContactImportService {
  private readonly http = inject(HttpClient);

  /**
   * Hands the file to the server and returns as soon as it is queued — the import itself runs in the
   * background and reports back through a notification.
   */
  startImport(file: File, createMissingSegments = false): Observable<ContactImportStartedDto> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    const params = new HttpParams().set('createMissingSegments', createMissingSegments);

    return this.http.post<ContactImportStartedDto>(
      `${environment.apis.default.url}/${IMPORT_API_BASE}/import-csv`,
      formData,
      { params },
    );
  }

  /**
   * Loads the stored per-row result of a finished import.
   *
   * Sent with `hide-errors` so `HttpErrorResponseInterceptor` passes the failure through instead of
   * swallowing it behind a toast: an expired result (422 /
   * `AgenticCX:Contact:ImportResultExpired`) is an expected outcome here and is rendered inside the
   * result dialog, not as an error toast.
   */
  getImportResult(importId: string): Observable<ContactImportResultDto> {
    const headers = new HttpHeaders().set('hide-errors', 'true');

    // `importId` goes in the path, NOT in the query string. ABP's ConventionalRouteBuilder appends a
    // "secondary id" segment when an action has exactly one required parameter whose name ends in `Id`,
    // so `GetImportResultAsync(Guid importId)` is published as `.../import-result/{importId}`. Sent as a
    // query param the URL is one segment short, falls through to the CRUD route `crm-contact/{id}`, and
    // fails with "The value 'import-result' is not valid." on `id`.
    return this.http.get<ContactImportResultDto>(
      `${environment.apis.default.url}/${IMPORT_API_BASE}/import-result/${importId}`,
      { headers },
    );
  }
}
